import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MessageCircle, ChevronLeft, Search, Plus, Layers, Trash2, ThumbsUp } from 'lucide-react';
import { useVerification } from '../../hooks/useVerification';
import { useUser } from '../../contexts/UserContext';
import { supabase } from '../../lib/supabase';
import PostEditor from '../common/PostEditor';
import TagInput from '../common/TagInput';
import { parseTags } from '../../lib/tags';
import { FormattedPreview } from '../common/FormattedText';
import PostImages from '../common/PostImages';
import { syncContentImages, imageErrorMessage, sweepImageDeletions } from '../../lib/postImages';
import SuggestVideoToLibrary, { EMPTY_SUGGESTION } from '../common/SuggestVideoToLibrary';
import { suggestVideoFromPost } from '../../lib/videoLibrary';
import TableIcon from '../common/TableIcon';
import VerifyModal from '../common/VerifyModal';
import CreatePostModal from '../common/CreatePostModal';
import useToast from '../../hooks/useToast';
import Toast from '../common/Toast';
import { getRoleLabel } from '../../lib/roleLabels';
import TeamTag from '../common/TeamTag';
import EditedMark from '../common/EditedMark';
import { draftKey, readDraft, writeDraft, clearDraft } from '../../lib/postDraft';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};


const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

const skeletonBg = 'var(--card-border)';

// ── Relative time helper ────────────────────────────────────

const relativeTime = (isoString) => {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(hours / 24);
  if (days >= 7) {
    // Short date beyond a week — a decaying "15w ago" counter reads as neglect
    const d = new Date(isoString);
    const opts = { month: 'short', day: 'numeric' };
    if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
    return d.toLocaleDateString('en-US', opts);
  }
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'just now';
};

// ── Skeleton components (module scope) ──────────────────────

const TableCardSkeleton = () => (
  <div className="rounded-xl overflow-hidden animate-pulse" style={cardStyle}>
    <div className="flex gap-0">
      <div className="w-24 sm:w-28 flex-shrink-0" style={{ backgroundColor: skeletonBg, minHeight: 100 }} />
      <div className="flex-1 p-3 sm:p-4 space-y-2">
        <div className="h-4 rounded w-3/5" style={{ backgroundColor: skeletonBg }} />
        <div className="h-3 rounded w-4/5" style={{ backgroundColor: skeletonBg }} />
        <div className="h-3 rounded w-2/5" style={{ backgroundColor: skeletonBg }} />
      </div>
    </div>
  </div>
);

const PostCardSkeleton = () => (
  <div className="rounded-2xl p-5 mb-4 animate-pulse" style={cardStyle}>
    <div className="flex gap-3 mb-3">
      <div className="w-10 h-10 rounded-full" style={{ backgroundColor: skeletonBg }} />
      <div className="flex-1 space-y-1.5">
        <div className="h-4 rounded w-1/3" style={{ backgroundColor: skeletonBg }} />
        <div className="h-3 rounded w-1/4" style={{ backgroundColor: skeletonBg }} />
      </div>
    </div>
    <div className="h-4 rounded w-2/3 mb-2" style={{ backgroundColor: skeletonBg }} />
    <div className="h-3 rounded w-full mb-1.5" style={{ backgroundColor: skeletonBg }} />
    <div className="h-3 rounded w-4/5" style={{ backgroundColor: skeletonBg }} />
  </div>
);

// ── TableCard (module scope — avoids remount on parent re-render) ──

const TableCard = ({ table, isMember }) => (
  <Link
    to={`/community/${table.slug}`}
    className="block rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] cursor-pointer"
    style={cardStyle}
  >
    <div className="flex items-center gap-3 p-3 sm:p-4">
      {/* Table icon art, emoji fallback */}
      <TableIcon table={table} px={56} />
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex items-center gap-1.5 mb-0.5">
          {table.sticky && <span className="text-xs" style={{ color: 'var(--brand-accent)' }}>📌</span>}
          {table.moderated && <span className="text-xs">🛡️</span>}
          <h3 className="font-bold text-sm text-primary leading-tight truncate">{table.name}</h3>
          {isMember && (
            <span
              className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
              style={{ backgroundColor: 'var(--brand-primary)', color: 'white' }}
            >
              Joined
            </span>
          )}
        </div>
        <p className="text-xs text-tertiary leading-snug line-clamp-2 mb-2.5">{table.description}</p>

        <div className="flex items-center gap-2 text-xs text-tertiary">
          {table.post_count === 0 ? (
            <span>No topics yet</span>
          ) : (
            <>
              <div>
                <span className="text-secondary font-semibold">{table.post_count}</span>
                <span className="ml-1">{table.post_count === 1 ? 'Topic' : 'Topics'}</span>
              </div>
              {table.last_post_at && (
                <>
                  <span className="opacity-40">·</span>
                  <span>Active {relativeTime(table.last_post_at)}</span>
                </>
              )}
            </>
          )}
        </div>

        {/* What's actually being discussed, rather than only the static
            description. Blocked authors are filtered out server-side (040), so
            this can fall back to an older topic or to nothing. */}
        {table.last_post_subject && (
          <p className="text-xs mt-1 truncate" style={{ color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--text-tertiary)' }}>Latest: </span>
            {table.last_post_subject}
          </p>
        )}
      </div>
    </div>
  </Link>
);

// ── PostCard (module scope — clickable card, navigates to post page) ──


const PostCard = ({ post, tableSlug, onNavigate, currentUserId, onDelete, onToggleHelpful }) => (
  <div
    className="rounded-2xl p-5 mb-4 transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] cursor-pointer"
    style={cardStyle}
    onClick={() => onNavigate(tableSlug, post.id)}
  >
    {/* Subject as headline. The card root keeps its onClick for tap
        convenience, but the title is a real anchor so the post can be
        right-clicked or cmd-clicked into a new tab — the root can't be one,
        it wraps buttons. */}
    <div className="flex items-start justify-between gap-2 mb-2">
      <Link
        to={`/community/${tableSlug}/${post.id}`}
        onClick={(e) => e.stopPropagation()}
        className="font-bold text-base leading-snug hover:underline"
        style={{ color: 'var(--text-primary)' }}
      >
        {post.subject}
      </Link>
      {currentUserId && post.author_id === currentUserId && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(post.id); }}
          className="p-1.5 rounded-lg flex-shrink-0 transition-all hover:scale-110 active:scale-95"
          style={{ color: 'var(--text-tertiary)' }}
          title="Delete post"
        >
          <Trash2 size={15} />
        </button>
      )}
    </div>

    {/* Author row — two lines, matching the feed card. See FeedScreen's note:
        the single-line version made role/agency the only shrinkable item. */}
    <div className="flex items-start gap-2.5 mb-3">
      {post.author_avatar ? (
        <img src={post.author_avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold bg-gradient-to-br from-brand-primary to-brand-secondary">
          {((post.author_use_username && post.author_username) ? post.author_username : (post.author_name || 'D'))[0]}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            to={`/profile/${post.author_id}`}
            onClick={(e) => e.stopPropagation()}
            className="block min-w-0 text-sm font-semibold text-primary truncate hover:underline transition-colors"
          >
            {(post.author_use_username && post.author_username) ? post.author_username : post.author_name}
          </Link>
          <TeamTag userId={post.author_id} />
        </div>
        <div className="flex items-center gap-1 text-xs min-w-0" style={{ color: 'var(--text-tertiary)' }}>
          {[getRoleLabel(post.author_role), post.author_agency].some(Boolean) && (
            <span className="truncate">
              {[getRoleLabel(post.author_role), post.author_agency].filter(Boolean).join(' · ')}
            </span>
          )}
          <span className="flex-shrink-0">
            {[getRoleLabel(post.author_role), post.author_agency].some(Boolean) ? '· ' : ''}{relativeTime(post.created_at)}
          </span>
          <EditedMark at={post.edited_at} />
        </div>
      </div>
    </div>

    {/* Content preview */}
    {/* Bold/italic/links kept; lists flatten to "• item" lines so the
        two-line clamp works on a single block. */}
    <FormattedPreview text={post.content} className="mb-3 leading-relaxed text-[15px] line-clamp-2 text-secondary" />

    <PostImages images={post.images} className="mb-3" />

    {/* Tags */}
    {post.tags && post.tags.length > 0 && (
      <div className="flex flex-wrap gap-2 mb-3">
        {post.tags.map(tag => (
          <span
            key={tag}
            className="px-2 py-1 rounded-full text-xs"
            style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-tertiary)' }}
          >
            #{tag}
          </span>
        ))}
      </div>
    )}

    {/* Stats row */}
    <div className="flex items-center gap-6 text-tertiary">
      {/* Was a bare 👍 span with no handler — tapping "helpful" here did
          nothing. Now a real toggle, matching the feed and post detail. */}
      {/* Your own post shows a plain count: you can't vote on it (052). */}
      {currentUserId && post.author_id === currentUserId ? (
        <span className="flex items-center gap-2 text-sm" title="Helpful votes on your post">
          <ThumbsUp size={18} aria-hidden="true" />
          <span className="font-medium">{post.helpful_count || 0}<span className="sr-only"> helpful votes</span></span>
        </span>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleHelpful(post.id); }}
          className="flex items-center gap-2 text-sm transition-all duration-200 hover:scale-110 active:scale-95"
          style={{ color: post.user_liked ? 'var(--brand-primary)' : undefined }}
          aria-label={post.user_liked ? 'Remove helpful' : 'Mark helpful'}
        >
          <ThumbsUp size={18} className={post.user_liked ? 'fill-current' : ''} />
          {post.helpful_count > 0 && <span className="font-medium">{post.helpful_count}</span>}
        </button>
      )}
      <span className="flex items-center gap-2 text-sm">
        <MessageCircle size={18} />
        <span>{post.reply_count} {post.reply_count === 1 ? 'reply' : 'replies'}</span>
      </span>
    </div>
  </div>
);

// ── Main component ──────────────────────────────────────────

const CommunityScreen = () => {
  const { tableName } = useParams(); // slug from URL
  const navigate = useNavigate();
  const { showVerifyModal, setShowVerifyModal, requireVerified } = useVerification();
  const { session, userData } = useUser();
  const { toastMsg, showToast } = useToast();
  const currentUserId = session?.user?.id ?? null;

  // ── Browse view state ───────────────────────────────────
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [membershipOverrides, setMembershipOverrides] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [browseTab, setBrowseTab] = useState('all');

  // ── Detail view state ───────────────────────────────────
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('recent');

  // ── Post creation ───────────────────────────────────────
  const [showNewPost, setShowNewPost] = useState(false);
  const [postSubject, setPostSubject] = useState('');
  const [postContent, setPostContent] = useState('');
  const [postTags, setPostTags] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [videoChoice, setVideoChoice] = useState(EMPTY_SUGGESTION);
  const [videoSuggestion, setVideoSuggestion] = useState(null);
  const handleVideoResolve = useCallback((s) => setVideoSuggestion(s), []);
  const [postDraftRestored, setPostDraftRestored] = useState(false);
  const [draftLoadedFor, setDraftLoadedFor] = useState(null);
  const [postSubmitting, setPostSubmitting] = useState(false);

  // ── Refs for subscription cleanup ───────────────────────
  const postsChannelRef = useRef(null);

  // ── Derived: find selected table by slug ────────────────
  // get_community_tables_with_membership returns only ACTIVE tables (040), so
  // an archived table — or a link shared before it was archived — needs the
  // single-row RPC. Fetched only on a miss, which is the rare case.
  const [archivedTable, setArchivedTable] = useState(null);
  const listedTable = tableName ? tables.find(t => t.slug === tableName) || null : null;
  const selectedTable = listedTable
    || (tableName && archivedTable?.slug === tableName ? archivedTable : null);

  useEffect(() => {
    if (!tableName || tablesLoading || listedTable) return;
    if (archivedTable?.slug === tableName) return;
    let cancelled = false;
    supabase.rpc('get_community_table', { p_slug: tableName }).then(({ data, error }) => {
      if (cancelled) return;
      if (error) { console.error('get_community_table:', error); return; }
      setArchivedTable(data?.[0] ?? null);
    });
    return () => { cancelled = true; };
  }, [tableName, tablesLoading, listedTable, archivedTable]);

  // ── effectiveMember helper ──────────────────────────────
  const effectiveMember = useCallback((table) => {
    if (membershipOverrides[table.id] !== undefined) return membershipOverrides[table.id];
    return table.is_member;
  }, [membershipOverrides]);

  // ── Fetch tables ────────────────────────────────────────
  const fetchTables = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_community_tables_with_membership');
    if (error) {
      console.error('get_community_tables_with_membership:', error);
      return;
    }
    setTables(data ?? []);
    setTablesLoading(false);
  }, []);

  // ── Fetch posts for a table ─────────────────────────────
  const fetchPosts = useCallback(async (tableId, sort) => {
    setPosts([]);
    setPostsLoading(true);

    const { data, error } = await supabase.rpc('get_table_posts', {
      p_table_id: tableId,
      p_sort: sort,
    });
    if (error) {
      console.error('get_table_posts:', error);
      setPostsLoading(false);
      return;
    }
    setPosts(data ?? []);
    setPostsLoading(false);
  }, []);

  // ── Mount: fetch tables + subscribe ─────────────────────
  useEffect(() => {
    fetchTables();

    const channel = supabase
      .channel('community-tables')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'community_tables',
      }, (payload) => {
        setTables(prev => prev.map(t =>
          t.id === payload.new.id
            ? { ...t, member_count: payload.new.member_count, post_count: payload.new.post_count, last_post_at: payload.new.last_post_at }
            : t
        ));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchTables]);

  // ── Detail view: fetch posts + subscribe when table changes
  useEffect(() => {
    if (!selectedTable) return;

    fetchPosts(selectedTable.id, sortBy);

    if (postsChannelRef.current) {
      supabase.removeChannel(postsChannelRef.current);
    }

    const channel = supabase
      .channel(`community-posts-${selectedTable.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'community_posts',
        filter: `table_id=eq.${selectedTable.id}`,
      }, () => {
        fetchPosts(selectedTable.id, sortBy);
      })
      .subscribe();

    postsChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      postsChannelRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTable?.id, sortBy]);

  // ── Join / Leave table ──────────────────────────────────

  const joinTable = useCallback(async (tableId) => {
    setMembershipOverrides(prev => ({ ...prev, [tableId]: true }));
    const { error } = await supabase.rpc('join_community_table', { p_table_id: tableId });
    if (error) {
      setMembershipOverrides(prev => { const n = { ...prev }; delete n[tableId]; return n; });
      showToast('Could not join table. Please try again.');
    }
  }, [showToast]);

  const leaveTable = useCallback(async (tableId) => {
    setMembershipOverrides(prev => ({ ...prev, [tableId]: false }));
    const { error } = await supabase.rpc('leave_community_table', { p_table_id: tableId });
    if (error) {
      setMembershipOverrides(prev => { const n = { ...prev }; delete n[tableId]; return n; });
      showToast('Could not leave table. Please try again.');
    }
  }, [showToast]);

  // ── Draft persistence for the inline compose form ───────
  // This is a SECOND compose surface, separate from CreatePostModal, and it
  // was missed when drafts were first added — a draft started here was lost on
  // Cancel or on leaving the table. Scoped per table so a draft for one table
  // never surfaces in another.

  const postDraftKey = selectedTable ? draftKey(`table:${selectedTable.slug}`) : null;

  // Restore when the table changes. Opens the form as well — leaving it closed
  // would hide the restored text and look exactly like the loss this fixes.
  useEffect(() => {
    if (!postDraftKey) return;
    const d = readDraft(postDraftKey);
    if (d) {
      setPostSubject(d.subject ?? '');
      setPostContent(d.content ?? '');
      setPostTags(d.tags ?? '');
      setPostImages(Array.isArray(d.images) ? d.images : []);
      setPostDraftRestored(true);
      setShowNewPost(true);
    } else {
      setPostDraftRestored(false);
    }
    setDraftLoadedFor(postDraftKey);
  }, [postDraftKey]);

  // ⚠️ Gated on draftLoadedFor, and that gate is load-bearing. Both effects run
  // in the same commit, and this one closes over the state from BEFORE the
  // restore above — so without the gate it would write an empty draft (i.e.
  // delete the stored one) on the very render that was restoring it.
  useEffect(() => {
    if (!postDraftKey || draftLoadedFor !== postDraftKey) return;
    writeDraft(postDraftKey, { subject: postSubject, content: postContent, tags: postTags, images: postImages });
  }, [postDraftKey, draftLoadedFor, postSubject, postContent, postTags, postImages]);

  const resetPostForm = useCallback(() => {
    if (postDraftKey) clearDraft(postDraftKey);
    setPostSubject('');
    setPostContent('');
    setPostTags('');
    setPostImages([]);
    setPostDraftRestored(false);
    setVideoChoice(EMPTY_SUGGESTION);
  }, [postDraftKey]);

  // ── Create post ─────────────────────────────────────────

  const handleCreatePost = useCallback(async () => {
    if (postSubmitting) return;   // ⌘/Ctrl+Enter bypasses the disabled button
    if (!requireVerified()) return;
    if (!postSubject.trim() || !postContent.trim()) {
      showToast('Subject and content are required.');
      return;
    }
    if (videoSuggestion && !videoSuggestion.category) {
      showToast('Choose a library category for the video, or untick the box.');
      return;
    }
    setPostSubmitting(true);

    const tags = parseTags(postTags);

    const { data: newPostId, error } = await supabase.rpc('create_post', {
      p_table_id: selectedTable.id,
      p_subject: postSubject.trim(),
      p_content: postContent.trim(),
      p_tags: tags,
    });

    if (error) {
      setPostSubmitting(false);
      const msg = error.message?.includes('not_verified')
        ? 'Verification required to post.'
        : error.message?.includes('table_archived')
        ? 'This table is archived — no new posts.'
        : 'Could not post. Please try again.';
      showToast(msg);
      return;
    }

    // Photos after the post, never before — see CreatePostModal.
    if (newPostId && postImages.length) {
      const { error: imgErr } = await syncContentImages({ postId: newPostId, images: postImages });
      if (imgErr) showToast(imageErrorMessage(imgErr.message));
    }

    // After the post exists — see CreatePostModal.
    const suggestionResult = newPostId && videoSuggestion
      ? await suggestVideoFromPost({ ...videoSuggestion, postId: newPostId })
      : null;
    setPostSubmitting(false);

    setShowNewPost(false);
    resetPostForm();

    if (newPostId) {
      navigate(`/community/${selectedTable.slug}/${newPostId}`, {
        state: suggestionResult ? { videoSuggestion: suggestionResult } : undefined,
      });
    } else {
      fetchPosts(selectedTable.id, sortBy);
    }
  }, [postSubmitting, requireVerified, postSubject, postContent, postTags, postImages, videoSuggestion, selectedTable, showToast, navigate, fetchPosts, sortBy, resetPostForm]);

  // ── Navigation helpers (stable callbacks for module-scope components) ──
  // Table and author navigation are plain <Link>s now, so only the post-card
  // body — which wraps buttons and therefore can't be an anchor — still needs
  // an imperative handler.

  const handleNavigatePost = useCallback((tableSlug, postId) => {
    navigate(`/community/${tableSlug}/${postId}`);
  }, [navigate]);

  const handleDeletePost = useCallback(async (postId) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    const { error } = await supabase.rpc('delete_post', { p_post_id: postId });
    if (error) { showToast('Could not delete post.'); return; }
    setPosts(prev => prev.filter(p => p.id !== postId));
    // The post's photos are now orphaned files; 041 queued them and this is
    // the author draining their own share of that queue.
    sweepImageDeletions().catch(() => {});
  }, [showToast]);

  // get_table_posts has always returned user_liked; this card just never used
  // it — the helpful affordance was a non-interactive span. Optimistic flip
  // then reconcile with the server count, matching FeedScreen's handleLike.
  const handleToggleHelpful = useCallback(async (postId) => {
    if (!requireVerified()) return;
    const flip = () => setPosts(prev => prev.map(p => (
      p.id === postId
        ? { ...p, user_liked: !p.user_liked, helpful_count: Math.max(0, (p.helpful_count || 0) + (p.user_liked ? -1 : 1)) }
        : p
    )));
    flip();
    const { data: newCount, error } = await supabase.rpc('toggle_post_like', { p_post_id: postId });
    if (error) {
      flip(); // revert
      showToast('Could not update. Please try again.');
    } else if (typeof newCount === 'number') {
      setPosts(prev => prev.map(p => (p.id === postId ? { ...p, helpful_count: newCount } : p)));
    }
  }, [requireVerified, showToast]);

  // ── Filter + sort tables ────────────────────────────────
  const filteredTables = searchQuery.trim()
    ? tables.filter(t =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (t.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : tables;

  const myTablesList = filteredTables.filter(t => effectiveMember(t));
  const discoverList = filteredTables.filter(t => !effectiveMember(t));
  const trendingList = [...filteredTables].sort((a, b) => {
    if (!a.last_post_at) return 1;
    if (!b.last_post_at) return -1;
    return new Date(b.last_post_at) - new Date(a.last_post_at);
  });

  // ── User initial for compose bar avatar ─────────────────
  const userInitial = (userData?.name || session?.user?.email || 'M')[0].toUpperCase();

  // ══════════════════════════════════════════════════════════
  // TABLE DETAIL VIEW
  // ══════════════════════════════════════════════════════════

  if (selectedTable) {
    const isMember = effectiveMember(selectedTable);
    // An archived table (040) stays readable at its URL — old links and
    // notification deep links still work — but nothing new can be written,
    // which a DB trigger enforces regardless of what this UI shows.
    const isArchived = selectedTable.active === false;

    return (
      // Extra bottom padding on mobile to clear BottomNav + pinned compose bar
      <div className="min-h-screen pb-[9rem] md:pb-6">
        {/* Mobile header */}
        <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
          <div className="max-w-2xl mx-auto px-4 py-2">
            <button
              onClick={() => navigate('/community')}
              className="flex items-center gap-1 mb-3 text-sm transition-colors text-white/70 hover:text-white"
            >
              <ChevronLeft size={18} />
              Back to Tables
            </button>

            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <TableIcon table={selectedTable} px={40} radius="rounded-lg" />
                  <h1 className="text-xl font-bold text-white">{selectedTable.name}</h1>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <span>{selectedTable.member_count} DSPs at this table</span>
                  <span>&bull;</span>
                  <span>{selectedTable.post_count} posts</span>
                </div>
              </div>
              <button
                onClick={() => isMember ? leaveTable(selectedTable.id) : joinTable(selectedTable.id)}
                hidden={isArchived}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-[0.98]"
                style={{
                  backgroundColor: isMember ? 'rgba(255,255,255,0.15)' : 'white',
                  color: isMember ? 'white' : 'var(--brand-primary)',
                  border: isMember ? '1px solid rgba(255,255,255,0.25)' : '1px solid transparent',
                }}
              >
                {isMember ? 'Leave table' : 'Join table'}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop page header */}
        <div className="hidden md:block max-w-2xl mx-auto px-4 pt-5 pb-3">
          <button
            onClick={() => navigate('/community')}
            className="flex items-center gap-1 mb-2 text-sm transition-colors hover:opacity-70"
            style={{ color: 'var(--text-secondary)' }}
          >
            <ChevronLeft size={16} />
            Back to Tables
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <TableIcon table={selectedTable} px={40} radius="rounded-lg" />
                <h1 className="text-xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>{selectedTable.name}</h1>
              </div>
              <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                <span>{selectedTable.member_count} DSPs at this table</span>
                <span>&bull;</span>
                <span>{selectedTable.post_count} posts</span>
              </div>
            </div>
            <button
              onClick={() => isMember ? leaveTable(selectedTable.id) : joinTable(selectedTable.id)}
              hidden={isArchived}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-[0.98]"
              style={{
                backgroundColor: isMember ? 'var(--pill-bg)' : 'var(--brand-primary)',
                color: isMember ? 'var(--text-secondary)' : 'white',
                border: '1px solid var(--card-border)',
              }}
            >
              {isMember ? 'Leave table' : 'Join table'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-2xl mx-auto px-4 py-5">
          {isArchived && (
            <div
              className="rounded-xl p-4 mb-5"
              style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
            >
              <p className="text-sm font-semibold text-primary">This table is archived</p>
              <p className="text-xs mt-1 leading-snug" style={{ color: 'var(--text-tertiary)' }}>
                You can still read everything here, but new topics and replies are closed.
              </p>
            </div>
          )}

          {/* Sort tabs */}
          <div className="flex gap-2 mb-5">
            {[
              { key: 'recent', label: 'Recent' },
              { key: 'helpful', label: 'Most Helpful' },
              { key: 'unanswered', label: 'Unanswered' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-[0.98]"
                style={{
                  backgroundColor: sortBy === key ? 'var(--brand-primary)' : 'var(--pill-bg)',
                  color: sortBy === key ? 'white' : 'var(--text-secondary)',
                  border: '1px solid ' + (sortBy === key ? 'transparent' : 'var(--pill-border)'),
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Inline compose trigger — all breakpoints now. It carries the
              inviting copy the removed mobile bar had; the FAB below is the
              always-reachable version once this scrolls away. */}
          {!showNewPost && !isArchived && (
            <button
              onClick={() => requireVerified() && setShowNewPost(true)}
              className="flex items-center gap-3 w-full p-4 rounded-2xl mb-5 text-left transition-all hover-surface"
              style={{
                border: '2px dashed var(--card-border)',
                backgroundColor: 'var(--surface-hover)',
              }}
            >
              <div
                className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold md:hidden"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                {userInitial}
              </div>
              <span style={{ color: 'var(--text-tertiary)' }}>Share your experience...</span>
            </button>
          )}

          {/* New post form */}
          {showNewPost && !isArchived && (
            <div className="rounded-2xl p-5 mb-5" style={cardStyle}>
              {postDraftRestored && (
                <div
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-xs mb-3"
                  style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: 'var(--text-tertiary)' }}
                >
                  <span>Draft restored.</span>
                  <button
                    onClick={resetPostForm}
                    className="font-semibold flex-shrink-0 transition-opacity hover:opacity-70"
                    style={{ color: 'var(--brand-accent-text)' }}
                  >
                    Start over
                  </button>
                </div>
              )}
              <input
                type="text"
                value={postSubject}
                onChange={(e) => setPostSubject(e.target.value)}
                placeholder="Subject"
                maxLength={200}
                className="w-full p-3 rounded-lg mb-3 text-sm text-primary font-medium focus:outline-none focus:ring-1 focus:ring-brand-secondary"
                style={{
                  backgroundColor: 'var(--input-bg)',
                  border: '1px solid var(--input-border)',
                }}
              />
              <div className="mb-3">
                <PostEditor
                  value={postContent}
                  onChange={setPostContent}
                  placeholder="Share your experience, ask a question, or offer support..."
                  ariaLabel="Message"
                  maxLength={5000}
                  onSubmit={handleCreatePost}
                  images={postImages}
                  onImagesChange={setPostImages}
                  userId={currentUserId}
                />
              </div>
              <TagInput value={postTags} onChange={setPostTags} className="mb-3" />
              <div className="mb-3 empty:hidden">
                <SuggestVideoToLibrary
                  subject={postSubject}
                  content={postContent}
                  value={videoChoice}
                  onChange={setVideoChoice}
                  onResolve={handleVideoResolve}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-tertiary">{postSubject.length}/200</span>
                <div className="flex gap-2">
                  {/* Closing keeps the draft — that is the whole point. Only
                      "Start over" on the restored banner clears it. */}
                  <button
                    onClick={() => setShowNewPost(false)}
                    className="px-4 py-2 rounded-lg text-sm transition-all active:scale-[0.98]"
                    style={{ backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePost}
                    disabled={postSubmitting || !postSubject.trim() || !postContent.trim()}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
                    style={{ backgroundColor: 'var(--brand-primary)' }}
                  >
                    {postSubmitting ? 'Posting...' : 'Post to table'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Posts list */}
          {postsLoading ? (
            <>
              <PostCardSkeleton />
              <PostCardSkeleton />
              <PostCardSkeleton />
            </>
          ) : posts.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                No posts yet. Be the first to start a conversation.
              </p>
            </div>
          ) : (
            posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                tableSlug={selectedTable.slug}
                onNavigate={handleNavigatePost}
                currentUserId={currentUserId}
                onDelete={handleDeletePost}
                onToggleHelpful={handleToggleHelpful}
              />
            ))
          )}
        </div>

        {/* Mobile FAB — same affordance as the feed, so "+ means post" carries
            across screens. Geometry (bottom-24 right-6, w-14 h-14) matches
            FeedScreen's FAB deliberately — they must not sit at different
            heights when switching tabs. Replaces a full-width pinned bar that sat above
            BottomNav and permanently covered the last post: pb-nav clears the
            nav only, and nothing accounted for the bar's own height. A corner
            FAB can't have that failure mode. The inline prompt at the top of
            the list keeps the inviting copy the bar was carrying. */}
        {!showNewPost && !isArchived && (
          <button
            onClick={() => requireVerified() && setShowNewPost(true)}
            className="fixed bottom-24 right-6 w-14 h-14 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full shadow-2xl z-50 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 group md:hidden"
            title="New topic"
            aria-label="New topic"
          >
            <Plus className="text-white transition-transform duration-300 group-hover:rotate-90" size={26} strokeWidth={2.5} />
          </button>
        )}

        {showVerifyModal && (
          <VerifyModal onClose={() => setShowVerifyModal(false)} />
        )}
        <Toast message={toastMsg} />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // BROWSE VIEW (default)
  // ══════════════════════════════════════════════════════════

  const BROWSE_TABS = [
    { key: 'all', label: 'All' },
    { key: 'joined', label: 'Joined' },
    { key: 'trending', label: 'Trending' },
  ];

  return (
    <div className="min-h-screen pb-nav">
      {/* Mobile header */}
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-white tracking-tight">Tables</h1>
            <button
              onClick={() => requireVerified() && setShowCreatePost(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-all hover:bg-white/25 active:scale-[0.98]"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)' }}
            >
              <Plus size={18} />
              New Topic
            </button>
          </div>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-2.5" size={18} style={{ color: 'rgba(255,255,255,0.6)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tables..."
              className="w-full pl-10 pr-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-white/40 placeholder:text-white/50"
              style={{
                backgroundColor: 'rgba(0,0,0,0.2)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
              }}
            />
          </div>
          {/* Tab bar */}
          <div className="flex gap-1 pb-1">
            {BROWSE_TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setBrowseTab(key)}
                className="px-3 py-1 rounded-lg text-xs font-medium transition-all active:scale-[0.97]"
                style={{
                  backgroundColor: browseTab === key ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
                  color: browseTab === key ? 'white' : 'rgba(255,255,255,0.6)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop toolbar */}
      <div className="hidden md:flex max-w-2xl mx-auto px-4 pt-5 pb-2 items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5" size={16} style={{ color: 'var(--text-tertiary)' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tables..."
            className="w-full pl-9 pr-4 py-2 rounded-lg text-sm focus:outline-none"
            style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--text-primary)' }}
          />
        </div>
        <button
          onClick={() => requireVerified() && setShowCreatePost(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white transition-all hover:brightness-110 active:scale-[0.98]"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          <Plus size={16} />
          New Topic
        </button>
      </div>

      {/* Desktop tab bar */}
      <div className="hidden md:flex max-w-2xl mx-auto px-4 pb-3 gap-2">
        {BROWSE_TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setBrowseTab(key)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-[0.98]"
            style={{
              backgroundColor: browseTab === key ? 'var(--brand-primary)' : 'var(--pill-bg)',
              color: browseTab === key ? 'white' : 'var(--text-secondary)',
              border: '1px solid ' + (browseTab === key ? 'transparent' : 'var(--pill-border)'),
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 pb-4 space-y-3">
        {tablesLoading ? (
          <>
            <TableCardSkeleton />
            <TableCardSkeleton />
            <TableCardSkeleton />
            <TableCardSkeleton />
          </>
        ) : (
          <>
            {/* ALL tab: My Tables + Discover */}
            {browseTab === 'all' && (
              <>
                {myTablesList.length > 0 && (
                  <div className="mb-2">
                    <div className="flex items-center gap-2 mb-3">
                      <Layers size={14} style={{ color: 'var(--brand-primary)' }} />
                      <h2 className="text-sm font-semibold text-tertiary uppercase tracking-wide">
                        My Tables ({myTablesList.length})
                      </h2>
                    </div>
                    <div className="space-y-3">
                      {myTablesList.map(table => (
                        <TableCard
                          key={table.id}
                          table={table}
                          isMember={true}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {discoverList.length > 0 && (
                  <div>
                    <h2 className="text-sm font-semibold mb-3 text-tertiary uppercase tracking-wide">
                      Discover
                    </h2>
                    <div className="space-y-3">
                      {discoverList.map(table => (
                        <TableCard
                          key={table.id}
                          table={table}
                          isMember={false}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {filteredTables.length === 0 && (
                  <div className="text-center py-10">
                    <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                      No tables found matching "{searchQuery}"
                    </p>
                  </div>
                )}
              </>
            )}

            {/* JOINED tab: only tables the user has joined */}
            {browseTab === 'joined' && (
              <>
                {myTablesList.length > 0 ? (
                  <div className="space-y-3">
                    {myTablesList.map(table => (
                      <TableCard
                        key={table.id}
                        table={table}
                        isMember={true}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-sm mb-3" style={{ color: 'var(--text-tertiary)' }}>
                      {searchQuery ? `No joined tables match "${searchQuery}"` : "You haven't joined any tables yet."}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => setBrowseTab('all')}
                        className="text-sm font-medium transition-colors hover:opacity-80"
                        style={{ color: 'var(--brand-primary)' }}
                      >
                        Discover tables →
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* TRENDING tab: all tables sorted by most recent activity */}
            {browseTab === 'trending' && (
              <>
                {trendingList.length > 0 ? (
                  <div className="space-y-3">
                    {trendingList.map(table => (
                      <TableCard
                        key={table.id}
                        table={table}
                        isMember={effectiveMember(table)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                      No tables found matching "{searchQuery}"
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {showCreatePost && (
        <CreatePostModal startStep="table" onClose={() => setShowCreatePost(false)} />
      )}
      {showVerifyModal && (
        <VerifyModal onClose={() => setShowVerifyModal(false)} />
      )}
      <Toast message={toastMsg} />
    </div>
  );
};

export default CommunityScreen;
